<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Button from '@/components/ui/Button.vue'
import { CustomerPages } from '@/router/customerRoutes'
import {
  LIVE_CLASS_DEV_SCENARIO_QUERY,
  LIVE_CLASS_DEV_SCENARIOS,
  persistLiveClassDevScenarioForNavigation,
  isLiveClassDevUiEnabled,
  type LiveClassDevScenarioId,
} from '@/utils/liveClassDevScenarios'

const route = useRoute()
const router = useRouter()

const classInstanceIdInput = ref('')
const selectedScenario = ref<LiveClassDevScenarioId>(LIVE_CLASS_DEV_SCENARIOS[0].id)

const devUiEnabled = isLiveClassDevUiEnabled()

watch(
  () => route.params.id,
  (id) => {
    if (typeof id === 'string' && id) {
      classInstanceIdInput.value = id
    }
  },
  { immediate: true },
)

const navigateToScenario = () => {
  const id = classInstanceIdInput.value.trim()
  if (!id) return
  persistLiveClassDevScenarioForNavigation(selectedScenario.value)
  void router.push({
    path: `${CustomerPages.LiveClass.path.replace(':id', id)}`,
    query: { [LIVE_CLASS_DEV_SCENARIO_QUERY]: selectedScenario.value },
  })
}
</script>

<template>
  <div class="live-class-dev-scenarios flex flex-col gap-4 text-left">
    <p v-if="!devUiEnabled" class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
      Live Class UI scenarios are only available in development builds, or when
      <code class="text-xs bg-white px-1 rounded">VITE_ENABLE_LIVE_CLASS_DEV_UI=true</code>
      is set.
    </p>
    <template v-else>
      <p class="text-sm text-gray-600">
        Skips registration and the Zoom SDK, but uses the same messages, layout, and side effects
        (for example redirect) as real flows. Uses placeholder webinar data only where the UI needs a
        join link.
      </p>
      <label class="flex flex-col gap-1 text-sm font-medium text-gray-800">
        Class instance ID
        <input
          v-model="classInstanceIdInput"
          type="text"
          class="border border-gray-300 rounded-md px-3 py-2 text-base font-normal"
          placeholder="Paste a real class_instance id from Firestore"
          autocomplete="off"
        />
      </label>
      <label class="flex flex-col gap-1 text-sm font-medium text-gray-800">
        Screen / scenario
        <select
          v-model="selectedScenario"
          class="border border-gray-300 rounded-md px-3 py-2 text-base font-normal bg-white"
        >
          <option v-for="opt in LIVE_CLASS_DEV_SCENARIOS" :key="opt.id" :value="opt.id">
            {{ opt.label }}
          </option>
        </select>
      </label>
      <p class="text-xs text-gray-500">
        {{ LIVE_CLASS_DEV_SCENARIOS.find((o) => o.id === selectedScenario)?.description }}
      </p>
      <Button
        variant="accent"
        class="self-start"
        :disabled="!classInstanceIdInput.trim()"
        @click="navigateToScenario"
      >
        Open live class
      </Button>
      <p class="text-xs text-gray-500 border-t border-gray-200 pt-3">
        Query param
        <code class="text-xs bg-gray-100 px-1 rounded">{{ LIVE_CLASS_DEV_SCENARIO_QUERY }}</code>
        is cleared from the URL when you use Try Again after a mock failure.
      </p>
    </template>
  </div>
</template>
